import { Pipe, PipeTransform } from '@angular/core';
import { floor } from 'lodash';
import * as moment from 'moment';

import { InspectionRecurrencePeriod } from '@main-application/inspections/models/rest-inspections-model.interface';
import { NoValue } from '@shared/constants/empty-value.const';

@Pipe({ name: 'recurrencePeriod' })
export class RecurrencePipe implements PipeTransform {
  transform(value: number, dueDate: Date | string): string {
    if (value == null || !dueDate) return NoValue;

    if (typeof dueDate === 'string') {
      dueDate = new Date(dueDate);
    }

    const recurrenceOptions = this.getRecurrenceOptions(dueDate);
    return (
      recurrenceOptions.find(option => option.value === value)?.label ??
      InspectionRecurrencePeriod[value]?.toLowerCase() ??
      NoValue
    );
  }

  private getRecurrenceOptions(dueDate: Date): { value: number; label: string }[] {
    const dayOfWeek = moment(dueDate).format('dddd');
    const weekNumber = floor(dueDate.getDate() / 7) + 1;
    let weekNumberText = 'N/A';

    switch (weekNumber) {
      case 1:
        weekNumberText = 'first';
        break;
      case 2:
        weekNumberText = 'second';
        break;
      case 3:
        weekNumberText = 'third';
        break;
      case 4:
        weekNumberText = 'fourth';
        break;
      case 5:
        weekNumberText = 'fifth';
        break;
    }

    const today = moment(dueDate).format('MMMM D');
    const semi1 = moment(dueDate).format('MMM D');
    const semi2 = moment(dueDate).add(7, 'month').format('MMM D');

    return [
      { value: InspectionRecurrencePeriod.DoNotRepeat, label: NoValue },
      { value: InspectionRecurrencePeriod.Daily, label: 'Weekdays' },
      { value: InspectionRecurrencePeriod.Weekly, label: `Weekly on ${dayOfWeek}` },
      { value: InspectionRecurrencePeriod.MonWedFri, label: `Weekly on Monday, Wednesday, Friday` },
      { value: InspectionRecurrencePeriod.MonFri, label: `Weekly on Monday, Friday` },
      { value: InspectionRecurrencePeriod.TueTh, label: `Weekly on Tuesday, Thursday` },
      { value: InspectionRecurrencePeriod.SemiMonthly, label: `Semi-monthly every other ${dayOfWeek}` },
      { value: InspectionRecurrencePeriod.Monthly, label: `Monthly on the first ${dayOfWeek}` },
      ...(weekNumber > 1
        ? [
            {
              value: InspectionRecurrencePeriod.SameWeekMonthly,
              label: `Monthly on the ${weekNumberText} ${dayOfWeek}`,
            },
          ]
        : []),
      { value: InspectionRecurrencePeriod.SemiAnnually, label: `Semi-annually on ${semi1} and ${semi2}` },
      { value: InspectionRecurrencePeriod.Quarterly, label: `Quarterly on the first ${dayOfWeek}` },
      ...(weekNumber > 1
        ? [
            {
              value: InspectionRecurrencePeriod.SameWeekQuarterly,
              label: `Quarterly on the ${weekNumberText} ${dayOfWeek}`,
            },
          ]
        : []),
      { value: InspectionRecurrencePeriod.Annually, label: `Annually on ${today}` },
      {
        value: InspectionRecurrencePeriod.EveryTwoYears,
        label: `Every 2 Years on ${today}`,
      },
      {
        value: InspectionRecurrencePeriod.EveryThreeYears,
        label: `Every 3 Years on ${today}`,
      },
      {
        value: InspectionRecurrencePeriod.EveryFourYears,
        label: `Every 4 Years on ${today}`,
      },
      {
        value: InspectionRecurrencePeriod.EveryFiveYears,
        label: `Every 5 Years on ${today}`,
      },
      {
        value: InspectionRecurrencePeriod.EveryTenYears,
        label: `Every 10 Years on ${today}`,
      },
    ];
  }
}
